import React from 'react';

import type { Text } from '@zillow/constellation';
import { FormField, Grid, Heading, Input, Label, Paragraph } from '@zillow/constellation';

import type { Section } from '../../../models/index';
import type { HTMLElementEvent } from '../../../models/Types';
import { PROPERTY_ADDRESS } from '../../../constants/index';
import { isMultiFamily } from './Utilities/MultiFamilyLandlordUtility';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function PropertyAddress_v3({ section, setSectionValues }: Props) {
  function onStreetChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.STREET]: value,
    });
  }

  function onAddressLine2Change(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.ADDRESS_LINE_2]: value,
    });
  }

  function onCityChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.CITY]: value,
    });
  }

  function onStateChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.STATE]: value,
    });
  }

  function onZipChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.ZIP]: value,
    });
  }

  function onMFStreetChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STREET]: value,
    });
  }

  function onMFAddressLine2Change(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ADDRESS_LINE_2]: value,
    });
  }

  function onMFCityChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_CITY]: value,
    });
  }

  function onMFStateChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STATE]: value,
    });
  }

  function onMFZipChange(event: HTMLElementEvent<typeof Text>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ZIP]: value,
    });
  }

  const isMultiFamilyLandlord = isMultiFamily(section);

  return (
    <>
      <Heading level={6}>What is the property’s address?</Heading>
      <FormField
        className={FULLSTORY_EXCLUDE}
        marginTop="xs"
        label={<Label>Street address</Label>}
        control={
          <Input
            aria-labelledby="property-address-street"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.STREET)}
            onChange={onStreetChange}
            error={
              section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)
                ? PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS
                : null
            }
          />
        }
      />
      <FormField
        className={FULLSTORY_EXCLUDE}
        marginTop="xs"
        label={<Label optional>Address line 2</Label>}
        control={
          <Input
            aria-labelledby="property-address-line-2"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.ADDRESS_LINE_2)}
            onChange={onAddressLine2Change}
          />
        }
      />
      <FormField
        className={FULLSTORY_EXCLUDE}
        marginTop="xs"
        label={<Label>City</Label>}
        control={
          <Input
            aria-labelledby="property-address-city"
            value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.CITY)}
            onChange={onCityChange}
          />
        }
      />
      <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="xs" marginTop="xs">
        <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
          <FormField
            className={FULLSTORY_EXCLUDE}
            label={<Label>State</Label>}
            control={
              <Input
                aria-labelledby="property-address-state"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.STATE)}
                onChange={onStateChange}
              />
            }
          />
        </Grid>
        <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
          <FormField
            className={FULLSTORY_EXCLUDE}
            label={<Label>ZIP Code</Label>}
            control={
              <Input
                aria-labelledby="property-address-zip"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.ZIP)}
                type="number"
                onChange={onZipChange}
              />
            }
          />
        </Grid>
      </Grid>
      {isMultiFamilyLandlord && (
        <>
          <Paragraph marginTop="md" fontType="h6">
            What is the multi-family property address?
          </Paragraph>
          <FormField
            className={FULLSTORY_EXCLUDE}
            marginTop="xs"
            label={<Label>Street address</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-street"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STREET)}
                onChange={onMFStreetChange}
                error={
                  section.hasFieldError(PROPERTY_ADDRESS.FIELD_NAME.PROPERTY_ADDRESS)
                    ? PROPERTY_ADDRESS.ERROR_MESSAGE.PROPERTY_ADDRESS
                    : null
                }
              />
            }
          />
          <FormField
            className={FULLSTORY_EXCLUDE}
            marginTop="xs"
            label={<Label optional>Address line 2</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-line-2"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ADDRESS_LINE_2)}
                onChange={onMFAddressLine2Change}
              />
            }
          />
          <FormField
            className={FULLSTORY_EXCLUDE}
            marginTop="xs"
            label={<Label>City</Label>}
            control={
              <Input
                aria-labelledby="property-address-multifamily-city"
                value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_CITY)}
                onChange={onMFCityChange}
              />
            }
          />
          <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="xs" marginTop="xs">
            <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
              <FormField
                className={FULLSTORY_EXCLUDE}
                label={<Label>State</Label>}
                control={
                  <Input
                    aria-labelledby="property-address-multifamily-state"
                    value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_STATE)}
                    onChange={onMFStateChange}
                  />
                }
              />
            </Grid>
            <Grid gridColumnEnd={{ default: 'span 12', sm_gte: 'span 6' }}>
              <FormField
                className={FULLSTORY_EXCLUDE}
                label={<Label>ZIP Code</Label>}
                control={
                  <Input
                    aria-labelledby="property-address-multifamily-zip"
                    value={section.getFieldValue(PROPERTY_ADDRESS.FIELD_NAME.MULTIFAMILY_ZIP)}
                    type="text"
                    onChange={onMFZipChange}
                  />
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

PropertyAddress_v3.description = 'Split out street address, city, state, zip';

export default PropertyAddress_v3;
